import type { StackProps } from "@chakra-ui/react"
import {
  Text,
  Link,
  HStack,
  useDisclosure,
  Button,
  Stack,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
} from "@chakra-ui/react"
import type { ChangeEvent, FC } from "react"
import { useMemo, useEffect, useState } from "react"
import { useFormikContext } from "formik"
import { useProvider } from "wagmi"
import type { BigNumberish } from "ethers"
import {
  ArrowForwardIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InfoOutlineIcon,
  WarningTwoIcon,
} from "@chakra-ui/icons"
import BigNumber from "bignumber.js"

import type { AssetForSelector } from "createProposal/components/actions/AssetSelectorWithInput"
import SwapRecipeInfo from "createProposal/components/actions/SwapRecipeInfo"
import type SwapAction from "createProposal/action/SwapAction"
import type { CreateProposalFormValues } from "createProposal/types/form"
import { useDynamicSlippageChecker } from "web3/hooks/useDynamicSlippageChecker"
import type { Pool, Token } from "query/uniswap"
import { useSwapPoolsQuery } from "query/uniswap"
import { WETH, ETH, UNI_V3_ADDRESS } from "createProposal/action/SwapAction"
import { EXTERNAL_ROUTES } from "common/constants/routes"

const getPoolTokens = (token: AssetForSelector | undefined): string[] => {
  if (!token) return []

  const { address } = token

  // cannot sell ETH, must sell wETH
  if (address === ETH) return [WETH.toLowerCase()]

  return [address.toLowerCase(), WETH.toLowerCase()]
}

type UniPriceCheckerProps = {
  action: Pick<
    SwapAction,
    | "amountIn"
    | "buyToken"
    | "getPriceCheckerData"
    | "quote"
    | "sellToken"
    | "slippage"
  >
  index: number
  buyAmount?: BigNumberish
}

const UniPriceChecker: FC<UniPriceCheckerProps & StackProps> = ({
  action,
  index,
  buyAmount,
  ...stackProps
}) => {
  const { amountIn, buyToken, quote, sellToken, slippage } = action

  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()
  const provider = useProvider()
  const checker = useDynamicSlippageChecker(UNI_V3_ADDRESS, provider)
  const [priceCheckerStatus, setPriceCheckerStatus] = useState<{
    isValid: boolean
    isLoading: boolean
    isError: boolean
  }>({
    isValid: false,
    isLoading: false,
    isError: false,
  })
  const [slippageValue, setSlippageValue] = useState(String(slippage / 100))

  const isFetchSwapPoolsEnabled = Boolean(sellToken && buyToken)
  const poolTokens = [...getPoolTokens(sellToken), ...getPoolTokens(buyToken)]
  const { data, isFetching } = useSwapPoolsQuery(
    {
      where: {
        // eslint-disable-next-line camelcase
        token0_in: poolTokens,
        // eslint-disable-next-line camelcase
        token1_in: poolTokens,
        // eslint-disable-next-line camelcase
        liquidity_gt: "0",
      },
    },
    {
      enabled: isFetchSwapPoolsEnabled,
    },
  )

  const path:
    | (Pick<Pool, "id" | "feeTier" | "liquidity"> & {
        token0: Pick<Token, "id" | "symbol">
        token1: Pick<Token, "id" | "symbol">
      })[] = useMemo(() => {
    if (!data?.pools || !sellToken || !buyToken) return []

    const { address: sellAddress } = sellToken
    const { address: buyAddress } = buyToken
    const wethAddress = WETH.toLowerCase()

    // cannot sell ETH, must sell wETH
    const sell = sellAddress === ETH ? wethAddress : sellAddress.toLowerCase()
    const buy = buyAddress.toLowerCase()

    const { pools } = data

    const liquiditySort = (
      a: Pick<Pool, "liquidity">,
      b: Pick<Pool, "liquidity">,
    ) => {
      const sub = new BigNumber(b.liquidity).minus(a.liquidity)

      if (sub.isNegative()) {
        return -1
      }

      return 1
    }

    const directs = pools
      .filter(
        ({ token0, token1 }) =>
          (token0.id === sell && token1.id === buy) ||
          (token0.id === buy && token1.id === sell),
      )
      .sort(liquiditySort)

    if (directs.length) {
      const [direct] = directs

      return [direct]
    }

    // if no direct pool is found, look for a multi-leg path via wETH
    const path1s = pools
      .filter(
        ({ token0, token1 }) =>
          (token0.id === sell && token1.id === wethAddress) ||
          (token0.id === wethAddress && token1.id === sell),
      )
      .sort(liquiditySort)

    const path2s = pools
      .filter(
        ({ token0, token1 }) =>
          (token0.id === buy && token1.id === wethAddress) ||
          (token0.id === wethAddress && token1.id === buy),
      )
      .sort(liquiditySort)

    if (!path1s.length || !path2s.length) return []

    const [path1] = path1s
    const [path2] = path2s

    return [path1, path2]
  }, [data, sellToken, buyToken])

  useEffect(() => {
    if (!isNaN(Number(slippageValue))) {
      setFieldValue(`actions.${index}.slippage`, Number(slippageValue) * 100)
    }
  }, [slippageValue, index, setFieldValue])

  useEffect(() => {
    setFieldValue(`actions.${index}.uniPoolPath`, path)

    const priceCheckerData = action.getPriceCheckerData(path)

    if (
      !checker ||
      !sellToken ||
      !buyToken ||
      !priceCheckerData ||
      !buyAmount
    ) {
      setPriceCheckerStatus((prev) => ({
        ...prev,
        isValid: false,
      }))

      return
    }

    setPriceCheckerStatus((prev) => ({
      ...prev,
      isLoading: true,
    }))

    checker
      .checkPrice(
        amountIn,
        sellToken.address,
        buyToken.address,
        0,
        buyAmount,
        priceCheckerData,
      )
      .then((isValid) =>
        setPriceCheckerStatus((prev) => ({
          ...prev,
          isValid,
        })),
      )
      .catch(() =>
        setPriceCheckerStatus((prev) => ({
          ...prev,
          isError: true,
        })),
      )
      .finally(() =>
        setPriceCheckerStatus((prev) => ({
          ...prev,
          isLoading: false,
        })),
      )

    // including `checker` creates infinite re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    path,
    index,
    setFieldValue,
    action,
    amountIn,
    sellToken,
    buyToken,
    buyAmount,
  ])

  const handleChangeSlippage = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    setSlippageValue(value)
  }

  const getInfoComponent = () => {
    switch (true) {
      case !isFetchSwapPoolsEnabled:
        return (
          <SwapRecipeInfo
            body={
              <Text>
                To validate the order, the buy and sell tokens must be defined.
              </Text>
            }
            colorScheme="orange"
            leftIcon={InfoOutlineIcon}
          />
        )

      case isFetching:
        return (
          <SwapRecipeInfo
            isLoading
            body={<Text>Fetching pools.</Text>}
            colorScheme="purple"
          />
        )

      case path.length === 0:
        return (
          <SwapRecipeInfo
            body={
              <Text>
                Learn how to define your price checker manually{" "}
                <Link
                  isExternal
                  color="purple.500"
                  href={`${EXTERNAL_ROUTES.tally.docs.swaps()}#using-the-custom-price-checker`}
                >
                  here
                </Link>
                .
              </Text>
            }
            colorScheme="red"
            leftIcon={WarningTwoIcon}
            title="No price checker pool path was found automatically"
          />
        )

      case priceCheckerStatus.isLoading:
        return (
          <SwapRecipeInfo
            isLoading
            body={<Text>Validating order.</Text>}
            colorScheme="purple"
          />
        )

      case priceCheckerStatus.isError:
        return (
          <SwapRecipeInfo
            body={<Text>Make sure you are on Ethereum mainnet.</Text>}
            colorScheme="red"
            leftIcon={WarningTwoIcon}
            title="There was an error calling the price checker"
          />
        )
      case !priceCheckerStatus.isValid:
        const slippageSuggestion = quote
          ? Math.round(
              new BigNumber(quote.feeAmount).div(amountIn).toNumber() * 100,
            ) + 1
          : undefined

        return (
          <SwapRecipeInfo
            body={
              <Text>
                This order won&apos;t fill at {Number(slippage) / 100}%
                slippage.{" "}
                {slippageSuggestion && `Try at least ${slippageSuggestion}%.`}{" "}
                Learn more about price checkers{" "}
                <Link
                  isExternal
                  color="purple.500"
                  href={`${EXTERNAL_ROUTES.tally.docs.swaps()}#what-is-a-price-checker`}
                >
                  here
                </Link>
                .
              </Text>
            }
            colorScheme="orange"
            leftIcon={WarningTwoIcon}
            title="Can't fill this order at current market conditions"
          />
        )
      default:
        return (
          <SwapRecipeInfo
            body={
              <Text>
                You can expect no more than {slippageValue}% slippage from
                market rates at the time of execution.
              </Text>
            }
            colorScheme="green"
            leftIcon={InfoOutlineIcon}
            title="Valid price checker"
          />
        )
    }
  }

  return (
    <Stack spacing={4} {...stackProps}>
      <FormControl>
        <FormLabel color="gray.700" fontSize="sm">
          Slippage
        </FormLabel>
        <InputGroup>
          <Input value={slippageValue} onChange={handleChangeSlippage} />
          <InputRightAddon>%</InputRightAddon>
        </InputGroup>
      </FormControl>
      <PoolPath path={path} />
      {getInfoComponent()}
    </Stack>
  )
}

export default UniPriceChecker

type PoolPathProps = {
  path:
    | (Pick<Pool, "id"> & {
        token0: Pick<Token, "id" | "symbol">
        token1: Pick<Token, "id" | "symbol">
      })[]
    | undefined
}

const PoolPath: FC<PoolPathProps> = ({ path }) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: false })

  if (!path) return null

  return (
    <Stack align="flex-start" spacing={0}>
      <Button
        fontWeight="bold"
        rightIcon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
        variant="link"
        onClick={onToggle}
      >
        View price checker pool path (advanced)
      </Button>
      {isOpen && (
        <HStack>
          {path.map((pool, index, path) => (
            <HStack key={pool.id}>
              <Link
                isExternal
                color="gray.500"
                fontSize="xs"
                href={`https://info.uniswap.org/#/pools/${pool.id}`}
              >
                {pool.token0.symbol} / {pool.token1.symbol}
              </Link>
              {index !== path.length - 1 && (
                <ArrowForwardIcon color="gray.500" />
              )}
            </HStack>
          ))}
        </HStack>
      )}
    </Stack>
  )
}
